import React from 'react';
import { NavLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { ClusterSwitcher } from '../wallet/ClusterSwitcher';
import { soundManager } from '../../utils/sounds';
import { shortKey, formatCurrency } from '../../utils/format';
import { useBalances } from '../../wallet/useBalances';
import { useCloseableMatches } from '../../api/matches';
import { isIOSSafari } from '../../hooks/useMobile';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  links: { to: string; label: string }[];
}

export const MobileMenu: React.FC<MobileMenuProps> = ({ isOpen, onClose, links }) => {
  const { publicKey, connected, disconnect, wallet, connecting } = useWallet();
  const { setVisible } = useWalletModal();
  const walletAddress = publicKey?.toBase58() ?? null;
  const { data: closeableMatches } = useCloseableMatches(walletAddress);
  const hasReclaimableRent = (closeableMatches?.length ?? 0) > 0;
  const { data: balances } = useBalances(walletAddress);

  const handleWalletClick = () => {
    if (connected) {
      disconnect();
    } else {
      setVisible(true);
    }
    onClose();
  };

  const handleLinkClick = () => {
    soundManager.play('select');
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed right-0 top-0 z-[101] h-full w-[280px] max-w-[85vw] bg-gradient-to-b from-[#1a0a2e] to-[#0c0c0f] border-l border-arcade-cyan/30 shadow-2xl pr-safe"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-arcade-cyan/20 px-4 py-4 pt-safe">
              <span className="font-display text-lg text-arcade-cyan">MENU</span>
              <button
                onClick={onClose}
                className="flex h-11 w-11 items-center justify-center rounded-lg border border-arcade-cyan/30 bg-black/40 text-arcade-cyan transition-colors hover:bg-arcade-cyan/10"
                aria-label="Close menu"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Wallet Section */}
            <div className="border-b border-arcade-cyan/20 p-4">
              {/* iOS Safari guidance */}
              {!connected && isIOSSafari() && (
                <div className="mb-3 rounded-lg border border-amber-400/30 bg-amber-500/10 px-3 py-2">
                  <p className="text-xs text-amber-300">
                    Open this site in your Phantom or Solflare app to connect your wallet
                  </p>
                </div>
              )}
              <button
                onClick={handleWalletClick}
                className="flex w-full items-center gap-3 rounded-xl border border-arcade-cyan/30 bg-gradient-to-r from-arcade-cyan/10 via-arcade-purple/10 to-joker/10 px-4 py-3 transition-all hover:border-arcade-cyan/60"
              >
                {connected ? (
                  <>
                    <span className="relative flex h-3 w-3">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-consumable opacity-75" />
                      <span className="relative inline-flex h-3 w-3 rounded-full bg-consumable" />
                    </span>
                    <div className="flex flex-col items-start">
                      <span className="text-[10px] uppercase tracking-wider text-white/40">
                        {wallet?.adapter.name || 'Wallet'}
                      </span>
                      <span className="font-mono text-sm font-medium text-white">
                        {publicKey ? shortKey(publicKey.toBase58()) : '—'}
                      </span>
                    </div>
                    <svg
                      className="ml-auto h-5 w-5 text-white/40"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                      />
                    </svg>
                  </>
                ) : (
                  <>
                    <div className="rounded-lg bg-arcade-cyan/20 p-2">
                      {connecting ? (
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                        >
                          <svg className="h-5 w-5 text-arcade-cyan" viewBox="0 0 24 24" fill="none">
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="3"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                        </motion.div>
                      ) : (
                        <svg
                          className="h-5 w-5 text-arcade-cyan"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                          />
                        </svg>
                      )}
                    </div>
                    <span className="font-arcade text-sm text-white">
                      {connecting ? 'Connecting...' : 'Connect Wallet'}
                    </span>
                    <svg
                      className="ml-auto h-5 w-5 text-arcade-cyan/60"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </>
                )}
              </button>
              {connected && balances && (
                <p className="mt-2 text-center font-mono text-xs text-white/50">
                  {formatCurrency(balances.sol, 'SOL')} | {formatCurrency(balances.usdc, 'USDC')}
                </p>
              )}
            </div>

            {/* Network Section - localhost only, for dev/testing */}
            {(window.location.hostname === 'localhost' ||
              window.location.hostname === '127.0.0.1') && (
              <div className="border-b border-arcade-cyan/20 px-4 py-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-white/40 uppercase tracking-wider">Network</span>
                  <ClusterSwitcher />
                </div>
              </div>
            )}

            {/* Navigation Links */}
            <nav className="overflow-y-auto p-4 max-h-[calc(100vh-250px)]">
              <div className="flex flex-col gap-1">
                {links.map(({ to, label }) => (
                  <NavLink
                    key={to}
                    to={to}
                    onClick={handleLinkClick}
                    className={({ isActive }) =>
                      `relative flex min-h-[44px] items-center rounded-lg px-4 py-3 font-display text-sm transition-all ${
                        isActive
                          ? 'bg-arcade-cyan text-black'
                          : 'text-arcade-cyan/80 hover:bg-arcade-cyan/10 hover:text-arcade-cyan'
                      }`
                    }
                  >
                    {label}
                    {to === '/wallet' && hasReclaimableRent && (
                      <span className="absolute right-3 flex h-3 w-3">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-600 opacity-75" />
                        <span className="relative inline-flex h-3 w-3 rounded-full bg-rose-600 border border-rose-400" />
                      </span>
                    )}
                  </NavLink>
                ))}
              </div>
            </nav>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
